/**
 * 资源页: 服务端分页 + 中英文搜索 + 服务端标签筛选.
 *
 * 两个接口分工, 不要混在一起:
 *   GET /courses/tags               筛选条数据(全站总数 + 各标签资源数), 与分页无关;
 *   GET /courses?tag=&limit=&offset 分页列表, 滚到列表底部自动加载下一页。
 *
 * 标签筛选在服务端完成, 所以列表里只会有当前标签的资源, 前端不做本地过滤,
 * 这样「全部 N / 各标签 N」也不依赖已加载的条数, 资源再多也不会少算。
 */

import { api, ApiError } from '../lib/api'
import { FREE_BADGE_CLASS, TAG_BADGE_CLASS } from '../lib/badges'
import { courseContent } from '../lib/content-language'
import { ensureCourseAccess } from '../lib/entitlement'
import { courseFavorites } from '../lib/favorites'
import { t, tTag } from '../lib/i18n'
import { goTo } from '../stores/router'
import { toast } from '../stores/toast'
import type { Course, CourseTagCount } from '../types/api'

/** 「全部」是伪标签, 不参与资源匹配 */
const ALL_TAGS = '全部'
/** 每页条数: 手机单列 24 张够滑一阵, 桌面三列刚好 8 行 */
const PAGE_SIZE = 24
/** 距列表底部多远开始预取下一页 */
const SENTINEL_MARGIN = '600px'

export function coursesPage() {
  // 非响应式状态放在闭包里: 写进组件状态会被 x-effect 追踪, 造成自我触发的请求风暴
  let inFlight = false
  let requestVersion = 0
  let observer: IntersectionObserver | null = null
  let sentinel: Element | null = null

  return {
    ...courseFavorites(),
    ...courseContent(),
    courses: [] as Course[],
    /** 当前筛选下的资源总数, 决定还有没有下一页 */
    total: 0,
    /** 全站已上架资源总数, 「全部」chip 显示它 */
    allTotal: 0,
    tagCounts: [] as CourseTagCount[],
    loading: false,
    loadingMore: false,
    loaded: false,
    error: '',
    activeTag: ALL_TAGS,
    searchDraft: '',
    keyword: '',
    /** PC 端: 标签默认单行(横向可滚), 展开后换成多行并显示收起按钮 */
    tagsExpanded: false,

    /** 进入资源页 / 切换标签时调用: 拉筛选条数据 + 当前筛选的第一页 */
    async load(): Promise<void> {
      // 延后读取筛选条件, 避免页面 x-effect 把表单状态也订阅成自动重载依赖.
      await Promise.resolve()
      const version = ++requestVersion
      const params = this.pageParams(0)
      inFlight = true
      this.loadingMore = false
      this.loading = true
      this.error = ''
      try {
        const [tags, page] = await Promise.all([
          api.courses.tags(),
          api.courses.list(params),
        ])
        if (version !== requestVersion) return
        this.tagCounts = tags.tags
        this.allTotal = tags.total
        this.courses = page.items
        this.total = page.meta.total
        this.loaded = true
        await this.loadSavedCourseIds()
      } catch (error) {
        if (version === requestVersion) {
          this.error = error instanceof ApiError ? error.message : '资源加载失败, 请稍后重试'
        }
      } finally {
        if (version === requestVersion) {
          inFlight = false
          this.loading = false
        }
      }
    },

    /** 请求参数: 标签筛选交给服务端 */
    pageParams(offset: number): Record<string, string | number> {
      const params: Record<string, string | number> = { limit: PAGE_SIZE, offset }
      if (this.activeTag !== ALL_TAGS) params['tag'] = this.activeTag
      if (this.keyword) params['keyword'] = this.keyword
      return params
    },

    /** 筛选条: 「全部」+ 服务端返回的标签(按资源数倒序) */
    get tagFilters(): string[] {
      return [ALL_TAGS, ...this.tagCounts.map((item) => item.tag)]
    },

    countByTag(tag: string): number {
      if (tag === ALL_TAGS) return this.allTotal
      return this.tagCounts.find((item) => item.tag === tag)?.count ?? 0
    },

    /** 点标签: 再点一次回到「全部」; 换筛选后从第一页重新加载 */
    setTag(tag: string): void {
      const next = this.activeTag === tag ? ALL_TAGS : tag
      if (next === this.activeTag) return
      this.activeTag = next
      void this.load()
    },

    /** 回车或搜索按钮提交, 中文输入法组词期间不发请求. */
    submitSearch(): void {
      this.keyword = this.searchDraft.trim()
      this.searchDraft = this.keyword
      void this.load()
    },

    /** 清空关键词仍保留当前标签筛选. */
    clearSearch(): void {
      this.searchDraft = ''
      this.keyword = ''
      void this.load()
    },

    get hasMore(): boolean {
      return this.courses.length < this.total
    },

    /** 加载下一页; 由列表底部的哨兵自动触发 */
    async loadMore(): Promise<void> {
      if (inFlight || this.loadingMore || !this.loaded || !this.hasMore) return
      const version = requestVersion
      this.loadingMore = true
      try {
        const page = await api.courses.list(this.pageParams(this.courses.length))
        if (version !== requestVersion) return
        this.courses = [...this.courses, ...page.items]
        this.total = page.meta.total
      } catch (error) {
        if (version === requestVersion) {
          toast(error instanceof ApiError ? error.message : '加载更多失败, 请重试')
        }
      } finally {
        if (version === requestVersion) this.loadingMore = false
        // 结果不足一屏时哨兵仍在视口内, 观察器不会再回调, 需要重新观察继续加载
        if (sentinel && observer) {
          observer.disconnect()
          observer.observe(sentinel)
        }
      }
    },

    /** 列表底部的哨兵进入视口就加载下一页(顺带支持键盘/读屏用户滚到底) */
    observeSentinel(el: Element | null): void {
      if (!el || typeof IntersectionObserver === 'undefined') return
      sentinel = el
      observer ??= new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) void this.loadMore()
        },
        { rootMargin: `${SENTINEL_MARGIN} 0px` },
      )
      observer.disconnect()
      observer.observe(el)
    },

    tagClass: TAG_BADGE_CLASS,
    freeBadgeClass: FREE_BADGE_CLASS,

    /** "全部"是前端伪标签; 业务标签按界面语言翻译(英文界面显示 History / Lifestyle ...) */
    tagLabel(tag: string): string {
      return tag === ALL_TAGS ? t('courses.all') : tTag(tag)
    },

    minutesOf(course: Course): number {
      return Math.max(1, Math.round(course.duration_seconds / 60))
    },

    open(course: Course): void {
      // 先判权限: 会员专享资源的媒体地址对非会员是空的, 否则会误报「内容接入中」
      if (!course.is_free && !ensureCourseAccess(course)) return
      if (
        (!course.media_url_360p && !course.media_url_480p && !course.media_url_720p) ||
        course.sentence_count === 0
      ) {
        toast('该资源内容接入中')
        return
      }
      goTo('practice', { id: String(course.id) })
    },
  }
}

// 调用方: index.html 的 x-data="coursesPage()"
